/**
 * Shared motion/animation primitives.
 *
 * The easing curves are also registered as CSS tokens in `tailwind.css`
 * (`--ease-out-strong`, `--ease-drawer`) for Tailwind utilities. motion/react
 * and the Web Animations API can't reference CSS variables, so these JS copies
 * are the single source of truth for animations driven from JavaScript — keep
 * the two in sync.
 */

/** Strong ease-out for UI enter/exit + hovers. Mirrors `--ease-out-strong`. */
export const EASE_OUT_STRONG: [number, number, number, number] = [
  0.23, 1, 0.32, 1,
]

/** iOS-style drawer/reveal curve as a CSS string (for WAAPI). Mirrors `--ease-drawer`. */
export const EASE_DRAWER = 'cubic-bezier(0.32, 0.72, 0, 1)'

/**
 * Tween durations in seconds, for motion/react JS configs only. Unlike the
 * easing curves above, these are intentionally NOT mirrored as CSS tokens —
 * CSS transitions use Tailwind's `duration-*` utilities, and the two systems are
 * kept separate on purpose (JS animates transform/opacity off the main thread;
 * CSS handles hover/color). Tweens are for opacity/color crossfades and
 * pointer-tracked morphs — anything that enters, moves, or responds to a
 * gesture should use a SPRING below instead.
 */
export const DURATION = {
  press: 0.15, // press / tap feedback
  tooltip: 0.15, // dock tooltip + small popovers
  enter: 0.2, // small element fade-in (e.g. inline messages)
  exit: 0.12, // small element fade-out — snappier than its enter
  cursor: 0.25, // pointer-tracked cursor morphs
} as const

/** Seconds between siblings in a staggered cascade. */
export const STAGGER = {
  tight: 0.05, // dense grids / lists
  photo: 0.06, // home photo row
} as const

/**
 * Spring presets (Apple-style visualDuration + bounce — easy to reason about).
 * Rule of thumb: spring anything that enters, moves, or responds to a gesture.
 */
export const SPRING = {
  hover: { type: 'spring' as const, visualDuration: 0.2, bounce: 0.1 }, // hover/press scale
  reveal: { type: 'spring' as const, visualDuration: 0.5, bounce: 0.15 }, // scroll/mount entrances
  modal: { type: 'spring' as const, visualDuration: 0.25, bounce: 0.12 }, // modal / dialog scale-in
} as const
